import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNotificationAdminConfigs } from "services/hooks/rentals";

const AdminConfigsTab = () => {
  const {
    adminConfigs,
    isLoadingAdminConfigs,
    errorAdminConfigs,
    isSubmittingAdminConfig,
    adminConfigActionError,
    createAdminConfig,
    updateAdminConfig,
    deleteAdminConfig,
  } = useNotificationAdminConfigs();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    admin_emails: "",
    admin_phone_numbers: "",
    is_active: true,
  });

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      related_organization: "",
      name: "",
      admin_emails: "",
      admin_phone_numbers: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      admin_emails: row.admin_emails || "",
      admin_phone_numbers: row.admin_phone_numbers || "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setMessage({ type: "error", text: "Config name is required." });
      return;
    }
    const payload = {
      related_organization: form.related_organization ? Number(form.related_organization) : null,
      name: form.name.trim(),
      admin_emails: form.admin_emails,
      admin_phone_numbers: form.admin_phone_numbers,
      is_active: form.is_active,
    };
    const response = editingId
      ? await updateAdminConfig(editingId, payload)
      : await createAdminConfig(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Admin config updated successfully." : "Admin config created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save admin config." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this admin config?")) return;
    const response = await deleteAdminConfig(id);
    if (response.success) {
      setMessage({ type: "success", text: "Admin config deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete admin config." });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Admin Recipient Configs</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Config
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorAdminConfigs && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load admin configs.
        </Alert>
      )}

      {adminConfigActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {adminConfigActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingAdminConfigs ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Emails</TableCell>
                <TableCell>Phones</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No admin configs found.</TableCell>
                </TableRow>
              ) : (
                adminConfigs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.related_organization ?? "Global"}</TableCell>
                    <TableCell>{row.admin_emails || "—"}</TableCell>
                    <TableCell>{row.admin_phone_numbers || "—"}</TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Admin Config" : "Create Admin Config"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Config Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Admin Emails (comma separated)"
            value={form.admin_emails}
            onChange={(event) => setForm((prev) => ({ ...prev, admin_emails: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Admin Phone Numbers (comma separated)"
            value={form.admin_phone_numbers}
            onChange={(event) => setForm((prev) => ({ ...prev, admin_phone_numbers: event.target.value }))}
          />
          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingAdminConfig}>
            {isSubmittingAdminConfig ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminConfigsTab;
